import React, { ReactNode, memo, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

export interface SidebarSubItemProps {
  label: string;
  url: string;
  selected?: boolean;
  onClick?: () => void;
}

export interface SidebarItemProps {
  label: string;
  icon?: ReactNode;
  url: string;
  target?: React.HTMLAttributeAnchorTarget;
  subItems?: Array<SidebarSubItemProps>;
  defaultExpanded?: boolean;
  selected?: boolean;
}

const ITEM_SX = { py: 0, px: 0 } as const;
const BUTTON_SX = { paddingLeft: 1.5, paddingRight: 0 } as const;
const ICON_SX = { '&.MuiListItemIcon-root': { minWidth: '40px' } } as const;
const SUB_ITEM_BUTTON_SX = { pl: 6, pr: 0 } as const;

function isRouteUrl(url: string) {
  return url.startsWith('/');
}

function getLinkProps(url: string, target?: React.HTMLAttributeAnchorTarget) {
  const targetProps = target ? { target, rel: target === '_blank' ? 'noopener noreferrer' : undefined } : {};

  return isRouteUrl(url) ? { component: Link, to: url, ...targetProps } : { component: 'a', href: url, ...targetProps };
}

const SidebarItem = memo(function SidebarItem({
  label,
  icon,
  url,
  target,
  subItems,
  defaultExpanded = false,
  selected,
}: SidebarItemProps) {
  const { hash, pathname, search } = useLocation();
  const isSelected = useMemo(
    () => selected ?? (url.startsWith('#') ? hash === url : `${pathname}${search}` === url),
    [hash, pathname, search, selected, url],
  );
  const hasSubItems = subItems && subItems.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleClick = hasSubItems
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        setExpanded((prev) => !prev);
      }
    : undefined;

  return (
    <>
      <ListItem sx={ITEM_SX}>
        <ListItemButton
          {...getLinkProps(url, target)}
          selected={!hasSubItems && isSelected}
          onClick={handleClick}
          sx={BUTTON_SX}
        >
          {icon && (
            <ListItemIcon
              sx={{
                ...ICON_SX,
                color: isSelected ? 'primary.main' : 'text.secondary',
              }}
            >
              {icon}
            </ListItemIcon>
          )}
          <ListItemText primary={label} />
          {hasSubItems && (expanded ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </ListItem>
      {hasSubItems && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {subItems.map((subItem, index) => {
              const isSubSelected = subItem.selected ?? hash === subItem.url;
              return (
                <ListItem key={`${label}-sub-${index}`} sx={ITEM_SX}>
                  <ListItemButton
                    {...getLinkProps(subItem.url)}
                    selected={isSubSelected}
                    onClick={subItem.onClick}
                    sx={SUB_ITEM_BUTTON_SX}
                  >
                    <ListItemText primary={subItem.label} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
});

export default SidebarItem;
